import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom"; // Added useNavigate
import axios from "axios";
import { fetchToken, getUserRole, getUserSecretariaId } from "./Auth";

function DetalhesExecucao() {
  const { id } = useParams();
  const navigate = useNavigate(); // Hook for redirection

  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projeto, setProjeto] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [userSecretariaId, setUserSecretariaId] = useState("");
  const [podeEditar, setPodeEditar] = useState(false);

  const [novoItem, setNovoItem] = useState({
    descricao: "",
    valor_total: "",
    unidade: "",
  });

  const [novaParcela, setNovaParcela] = useState({
    item_id: null,
    mes: "",
    valor_simples: "",
  });

  const [paginaMeses, setPaginaMeses] = useState(0);
  const totalPaginas = 3;
  const mesesPorPagina = [12, 12, 6];

  const getMeses = () => {
    const inicio = paginaMeses === 0 ? 1 : paginaMeses === 1 ? 13 : 25;
    const fim = inicio + mesesPorPagina[paginaMeses] - 1;
    return Array.from({ length: fim - inicio + 1 }, (_, idx) => inicio + idx);
  };

  useEffect(() => {
    const role = getUserRole() || "";
    const secretariaId = getUserSecretariaId() || "";
    setUserRole(role);
    setUserSecretariaId(secretariaId);
  }, []);

  useEffect(() => {
    const fetchDados = async () => {
      setLoading(true);

      const token = fetchToken();
      if (!token) {
        setLoading(false);
        alert("Sessão expirada. Faça login novamente.");
        navigate("/login"); // Redirect to login
        return;
      }

      try {
        // Fetch Project Data (Added token header just in case backend requires it for read)
        const resProj = await axios.get(
          `/api/projetocompleto/${id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (resProj.data.Status === "Success" && resProj.data.Result.length) {
          const proj = resProj.data.Result[0];
          setProjeto(proj);

          // Permission Logic
          let isAuthorized = false;
          if (userRole === "admin") {
            isAuthorized = true;
          } else if (Number(proj.id_secretaria) === Number(userSecretariaId)) {
            isAuthorized = true;
          } else if (
            proj.equipe &&
            proj.equipe.some(
              (member) =>
                member.id ===
                /* User ID logic needed here if available in frontend context */ 0,
            )
          ) {
            // Note: Ideally you need the current user's ID to check against proj.equipe
            // For now, relying on backend 403 for writes is safer, but UI hiding can use:
            isAuthorized = true; // Placeholder if you implement team check in UI
          }

          // Simplest check based on your previous code:
          setPodeEditar(
            userRole === "admin" ||
              Number(proj.id_secretaria) === Number(userSecretariaId),
            // Add team check here if you have currentUserId available
          );
        }
        await fetchItens(token);
      } catch (err) {
        console.error("Erro ao buscar projeto:", err);
        if (
          err.response &&
          (err.response.status === 401 || err.response.status === 403)
        ) {
          alert("Acesso negado.");
          navigate("/projetocompleto");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchDados();
  }, [id, userRole, userSecretariaId, navigate]);

  const fetchItens = async (token) => {
    const authToken = token || fetchToken();
    if (!authToken) return;

    try {
      const res = await axios.get(`/api/cronograma/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const dados = res.data || [];
      const itensOrdenados = dados.sort(
        (a, b) => (a.item_num || 0) - (b.item_num || 0),
      );
      setItens(itensOrdenados);
    } catch (err) {
      console.error("Erro ao buscar cronograma:", err);
    }
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    const valor_total = parseFloat(novoItem.valor_total);
    if (!novoItem.descricao || isNaN(valor_total)) {
      return alert("Preencha todos os campos corretamente");
    }

    const token = fetchToken();
    if (!token) {
      alert("Sessão expirada.");
      navigate("/login");
      return;
    }

    try {
      const maxItemNum = itens.reduce(
        (acc, item) => Math.max(acc, item.item_num || 0),
        0,
      );
      await axios.post(
        `/api/cronograma`,
        {
          projeto_id: Number(id),
          item_num: maxItemNum + 1,
          descricao: novoItem.descricao,
          valor_total,
          unidade: novoItem.unidade,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      await fetchItens(token);
      setNovoItem({ descricao: "", valor_total: "", unidade: "" });
      // Close modal programmatically if needed, or rely on Bootstrap attributes
    } catch (err) {
      console.error("Erro ao criar item:", err);
      alert("Erro ao criar item");
    }
  };

  const handleExcluirItem = (idItem) => {
    if (!window.confirm("Confirma exclusão do item?")) return;

    const token = fetchToken();
    if (!token) return alert("Sessão expirada.");

    axios
      .delete(`/api/cronograma/${idItem}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setItens((prevItens) => prevItens.filter((item) => item.id !== idItem));
        if (novaParcela.item_id === idItem) {
          setNovaParcela({ item_id: null, mes: "", valor_simples: "" });
        }
        alert("Item e suas parcelas excluídos com sucesso!");
      })
      .catch(() => {
        alert("Não foi possível excluir o item");
      });
  };

  const handleSubmitParcela = async (e) => {
    e.preventDefault();
    const valor_simples = parseFloat(novaParcela.valor_simples);
    const mes = parseInt(novaParcela.mes);

    if (!novaParcela.item_id || isNaN(valor_simples) || isNaN(mes)) {
      return alert("Preencha todos os campos da parcela");
    }

    const token = fetchToken();
    if (!token) return alert("Sessão expirada.");

    try {
      await axios.post(
        `/api/cronograma/mensal`,
        {
          item_id: novaParcela.item_id,
          mes,
          valor_simples,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      await fetchItens(token);
      setNovaParcela({ item_id: null, mes: "", valor_simples: "" });
    } catch (err) {
      alert("Erro ao criar parcela");
    }
  };

  if (loading)
    return (
      <div className="text-center mt-4">
        <div className="spinner-border text-primary"></div> Carregando...
      </div>
    );

  return (
    <div className="container-fluid p-4 bg-light">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3>Cronograma Físico-Financeiro</h3>
          <p className="text-muted">{projeto?.nomeprojeto}</p>
        </div>
        <Link
          to={`/viewprojetocompleto/${id}`}
          className="btn btn-secondary btn-sm"
        >
          Voltar
        </Link>
      </div>

      <div className="mb-3">
        {Array.from({ length: totalPaginas }, (_, i) => (
          <button
            key={i}
            className={`btn btn-sm me-2 ${
              i === paginaMeses ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => setPaginaMeses(i)}
          >
            {i === 0 ? "M1-M12" : i === 1 ? "M13-M24" : "M25-M30"}
          </button>
        ))}
      </div>

      {podeEditar && (
        <button
          className="btn btn-success btn-sm mb-3"
          data-bs-toggle="modal"
          data-bs-target="#modalItem"
        >
          + Item
        </button>
      )}

      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th>Item</th>
              <th>Unidade</th>
              <th>Total (R$)</th>
              {getMeses().map((mes) => (
                <th key={mes} className="text-center">{`M${mes}`}</th>
              ))}
              {podeEditar && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <tr key={item.id}>
                <td>{item.descricao}</td>
                <td>{item.unidade}</td>
                <td>
                  {parseFloat(item.valor_total || 0).toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                  })}
                </td>
                {getMeses().map((mes) => {
                  const p = (item.mensal || []).find((x) => x.mes === mes);
                  return (
                    <td key={mes} className="text-end">
                      {p
                        ? parseFloat(p.valor_simples || 0).toLocaleString(
                            "pt-BR",
                            { minimumFractionDigits: 2 },
                          )
                        : "-"}
                    </td>
                  );
                })}
                {podeEditar && (
                  <td>
                    <button
                      className="btn btn-danger btn-sm me-1"
                      onClick={() => handleExcluirItem(item.id)}
                    >
                      Excluir
                    </button>
                    <button
                      className="btn btn-success btn-sm"
                      data-bs-toggle="modal"
                      data-bs-target="#modalParcela"
                      onClick={() =>
                        setNovaParcela({
                          item_id: item.id,
                          mes: "",
                          valor_simples: "",
                        })
                      }
                    >
                      + Parcela
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Novo Item */}
      <div className="modal fade" id="modalItem" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <form onSubmit={handleSubmitItem}>
              <div className="modal-header">
                <h5 className="modal-title">Novo Item</h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  onClick={() =>
                    setNovoItem({ descricao: "", valor_total: "", unidade: "" })
                  }
                ></button>
              </div>
              <div className="modal-body">
                {["descricao", "valor_total", "unidade"].map((campo, idx) => (
                  <div className="mb-3" key={idx}>
                    <label>{campo.replace("_", " ").toUpperCase()}</label>
                    <input
                      type={campo === "valor_total" ? "number" : "text"}
                      step={campo === "valor_total" ? "0.01" : undefined}
                      className="form-control"
                      value={novoItem[campo]}
                      onChange={(e) =>
                        setNovoItem({
                          ...novoItem,
                          [campo]: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-success btn-sm">
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal Nova Parcela */}
      <div className="modal fade" id="modalParcela" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form onSubmit={handleSubmitParcela}>
              <div className="modal-header">
                <h5 className="modal-title">
                  Adicionar Parcela:{" "}
                  {novaParcela.item_id &&
                    itens.find((i) => i.id === novaParcela.item_id)?.descricao}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  onClick={() =>
                    setNovaParcela({
                      item_id: null,
                      mes: "",
                      valor_simples: "",
                    })
                  }
                ></button>
              </div>
              <div className="modal-body">
                {["mes", "valor_simples"].map((campo, idx) => (
                  <div className="mb-3" key={idx}>
                    <label>{campo.replace("_", " ").toUpperCase()}</label>
                    <input
                      type="number"
                      min={campo === "mes" ? 1 : undefined}
                      max={campo === "mes" ? 30 : undefined}
                      step={campo === "valor_simples" ? "0.01" : 1}
                      className="form-control"
                      value={novaParcela[campo]}
                      onChange={(e) =>
                        setNovaParcela({
                          ...novaParcela,
                          [campo]: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-success btn-sm">
                  Salvar Parcela
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DetalhesExecucao;
